import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertBanner, Button, PageHeader, Stepper, StepperNavFooter } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowLeft } from 'lucide-react';

import {
  selectCheckBlockers,
  selectCheckSummary,
  selectMappingErrors,
  selectPrimaryLabel,
  useImportWizard,
} from '../../../../features/members-import/state';
import { IMPORT_ACTIONS, IMPORT_STEP_ORDER, IMPORT_STEPS } from '../../constants';
import { useImportCatalog, usePeopleAccessPaths, useSubmitImport } from '../../hooks';
import DiscardImportModal from './DiscardImportModal.jsx';
import ImportMatchStep from './ImportMatchStep.jsx';
import ImportCheckStep from './check/ImportCheckStep.jsx';
import ImportReviewStep from './review/ImportReviewStep.jsx';
import ImportUploadStep from './ImportUploadStep.jsx';
import { stepStateOf } from '../../utils/stepState';
import FullPageFrame from '../shared/FullPageFrame';

/** Why Next is blocked on the current step, or `null` when the user may continue. */
function nextBlockedReason(state, t) {
  if (state.step === IMPORT_STEPS.UPLOAD && !state.file) return t('import.nextDisabledReason');
  if (state.step === IMPORT_STEPS.MATCH && selectMappingErrors(state).length > 0) {
    return t('import.match.nextDisabledReason');
  }
  if (state.step === IMPORT_STEPS.CHECK) {
    if (selectCheckBlockers(state).total > 0) return t('import.check.blockedReason');
    const { ready, updates } = selectCheckSummary(state);
    if (ready + updates === 0) return t('import.check.nothingToAdd');
  }
  return null;
}

/**
 * Full-page shell of "Add members from a file": header, stepper, the current step and the
 * Back / Next footer. Back on the first step leaves the wizard, asking first when a file is loaded.
 */
function ImportWizard() {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { membersPath } = usePeopleAccessPaths();
  const { loadCatalog } = useImportCatalog();
  const { state, dispatch, setStep, reset, parsing } = useImportWizard();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const currentIndex = IMPORT_STEP_ORDER.indexOf(state.step);
  const isFirst = currentIndex === 0;
  const hasFile = state.file !== null;

  const steps = useMemo(
    () =>
      IMPORT_STEP_ORDER.map((id, index) => ({
        id,
        label: t(`import.steps.${id}`),
        state: stepStateOf(index, currentIndex),
      })),
    [t, currentIndex],
  );

  const leave = useCallback(() => navigate(membersPath), [navigate, membersPath]);
  const handleBack = useCallback(() => {
    setLoadFailed(false);
    if (!isFirst) setStep(IMPORT_STEP_ORDER[currentIndex - 1]);
    else if (hasFile) setDiscardOpen(true);
    else leave();
  }, [isFirst, hasFile, currentIndex, setStep, leave]);
  const handleNext = useCallback(async () => {
    const next = IMPORT_STEP_ORDER[currentIndex + 1];
    // Leaving the match step: check the file against the roles, groups and members once.
    // Rows are rebuilt only when the mapping changed (`SET_MAPPING` drops the catalog); a list the
    // admin emptied by hand is not rebuilt.
    if (state.step === IMPORT_STEPS.MATCH && state.catalog === null) {
      setChecking(true);
      setLoadFailed(false);
      let catalog;
      try {
        catalog = await loadCatalog();
      } catch {
        if (mountedRef.current) setLoadFailed(true);
        return;
      } finally {
        if (mountedRef.current) setChecking(false);
      }
      if (!mountedRef.current) return;
      dispatch({ type: IMPORT_ACTIONS.BUILD_ROWS, payload: { catalog } });
    }
    setStep(next);
  }, [currentIndex, state.step, state.catalog, loadCatalog, dispatch, setStep]);
  const handleKeep = useCallback(() => setDiscardOpen(false), []);
  const handleDiscard = useCallback(() => {
    setDiscardOpen(false);
    reset();
    leave();
  }, [reset, leave]);

  // Holding Next while a file is read keeps the upload step mounted until its result (or error) lands.
  const blockedReason = parsing ? t('import.upload.parsing') : nextBlockedReason(state, t);
  const isLast = state.step === IMPORT_STEPS.CHECK;
  const { submit, submitting, failed } = useSubmitImport(state);
  const label = isLast ? selectPrimaryLabel(state) : null;

  return (
    <div data-testid="page-members-import" className="flex min-h-full flex-col">
      <FullPageFrame>
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-start gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={ArrowLeft}
              aria-label={t('import.back')}
              onClick={handleBack}
            />
            <PageHeader title={t('import.title')} subtitle={t('import.subtitle')} className="flex-1" />
          </div>
          {failed ? (
            <AlertBanner tone="danger" title={t('import.check.result.failedTitle')}>
              {t('import.check.result.failedMessage')}
            </AlertBanner>
          ) : null}
          {loadFailed ? (
            <AlertBanner tone="danger" title={t('import.review.loadError')} />
          ) : null}
          <Stepper
            steps={steps}
            onStepClick={setStep}
            ariaLabel={t('import.stepperLabel')}
          />
          {state.step === IMPORT_STEPS.UPLOAD ? <ImportUploadStep /> : null}
          {state.step === IMPORT_STEPS.MATCH ? <ImportMatchStep /> : null}
          {state.step === IMPORT_STEPS.REVIEW ? <ImportReviewStep /> : null}
          {state.step === IMPORT_STEPS.CHECK ? <ImportCheckStep /> : null}
        </div>
      </FullPageFrame>
      <StepperNavFooter
        ariaLabel={t('import.footerLabel')}
        backLabel={isFirst ? t('import.cancel') : t('import.back')}
        backVariant={isFirst ? 'cancel' : 'back'}
        onBack={handleBack}
        primaryLabel={isLast ? t(`import.check.primary.${label.key}`, label) : t('import.next')}
        primaryIcon={isLast ? null : undefined}
        onPrimary={isLast ? submit : handleNext}
        busy={submitting}
        primaryLoading={checking || submitting}
        primaryDisabled={Boolean(blockedReason)}
        primaryDisabledReason={blockedReason ?? undefined}
      />
      <DiscardImportModal isOpen={discardOpen} onKeep={handleKeep} onDiscard={handleDiscard} />
    </div>
  );
}

export default ImportWizard;
